// When the donation page runs inside a campaign's embed iframe, it reports its content
// height to the parent so the embed snippet's listener can resize the iframe. The
// 3-stage form changes height between stages; a fixed-height iframe would clip or
// scroll, so we post the live height on every content resize.

import { useEffect } from 'react';
import { SUBSIDIARY_CODE } from '@/config';
import { getLegalConfig } from '@/config/customerConfig';
import { donationEmbedHeightMessage } from './donationEmbedSnippet';

// The parent snippet's listener was baked with this same subsidiary brand (both
// ends run the same build), so the message type always matches on the other side.
const HEIGHT_MESSAGE = donationEmbedHeightMessage(
  getLegalConfig(SUBSIDIARY_CODE).companyName.toLowerCase(),
);

function postDonationEmbedHeight(): void {
  if (typeof window === 'undefined' || window.parent === window) return;
  const height = Math.ceil(document.documentElement.scrollHeight);
  // Target '*' — the payload is a bare height, not sensitive. The parent listener
  // verifies the message came from this iframe's contentWindow before trusting it.
  window.parent.postMessage({ type: HEIGHT_MESSAGE, height }, '*');
}

export function useDonationEmbedResize(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    postDonationEmbedHeight();
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => postDonationEmbedHeight()) : null;
    observer?.observe(document.documentElement);
    window.addEventListener('load', postDonationEmbedHeight);
    return () => {
      observer?.disconnect();
      window.removeEventListener('load', postDonationEmbedHeight);
    };
  }, [enabled]);
}
